'use client';

import * as React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { Typography } from '@/components/ui/typography';
import { cn } from '@/lib/utils';
import { HeroBanner, type HeroBannerPageTitle } from '@/components/frontend/common/hero-banner';

type FaqItem = {
  id: string;
  question: string;
  answer: string;
};

export function FaqContent({ showHeading = true, className, limitItems = false, initialItems }: { showHeading?: boolean; className?: string; limitItems?: boolean; initialItems?: FaqItem[] }) {
  const t = useTranslations('home.faq');
  const locale = useLocale();
  const [openId, setOpenId] = React.useState<string>('q1');
  const [items, setItems] = React.useState<FaqItem[]>(() => (initialItems?.length ? (limitItems ? initialItems.slice(0, 5) : initialItems) : []));
  const [isLoading, setIsLoading] = React.useState(!initialItems?.length);

  React.useEffect(() => {
    if (initialItems?.length) {
      setItems(limitItems ? initialItems.slice(0, 5) : initialItems);
      setIsLoading(false);
      return;
    }
    const load = async () => {
      try {
        const timestamp = Date.now();
        const res = await fetch(`/api/faq?locale=${encodeURIComponent(locale)}&_t=${timestamp}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!res.ok) throw new Error('Failed to load FAQ');
        const data = await res.json();
        if (Array.isArray(data?.items) && data.items.length) {
          const itemsToShow = limitItems ? data.items.slice(0, 5) : data.items;
          setItems(
            itemsToShow.map((item: any, idx: number) => ({
              id: item.id || `faq-${idx}`,
              question: item.question || '',
              answer: item.answer || '',
            }))
          );
        } else {
          setItems([]);
        }
      } catch {
        setItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [limitItems, locale, initialItems?.length]);

  const toggle = (id: string) => setOpenId((prev) => (prev === id ? '' : id));

  return (
    <div className={cn('max-w-4xl mx-auto space-y-6', className)}>
      {showHeading ? (
        <div className="text-center space-y-4">
          <SectionHeading
            page="faq"
            align="center"
            maxWidthClass="max-w-4xl"
            fallbackPill={t('pill')}
            fallbackTitle={t('title')}
            fallbackSubtitle={t('leadQuestion')}
          />
        </div>
      ) : null}

      <div className="space-y-4">

        {!isLoading && items.length === 0 && (
          <div className="text-center text-gray-500 dark:text-gray-400 py-6">{t('noFaqsAvailable')}</div>
        )}

        {items.map((item) => {
          const isOpen = openId === item.id;
          return (
            <div
              key={item.id}
              className={cn(
                'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1a1f2a] text-left shadow-lg transition-all duration-300 ease-out',
                isOpen && 'border-primary-600 shadow-primary-500/15'
              )}
            >
              <button
                type="button"
                onClick={() => toggle(item.id)}
                className="w-full flex items-center justify-between px-6 py-5 text-left gap-4 border-b border-gray-100 dark:border-gray-800/80"
                aria-expanded={isOpen}
              >
                <Typography variant="p" className="text-lg font-semibold text-gray-900 dark:text-white">
                  {item.question}
                </Typography>
                {isOpen ? (
                  <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-300" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-300" />
                )}
              </button>
              <div
                className={cn(
                  'px-6 text-gray-700 dark:text-gray-300 leading-relaxed transition-all duration-300 ease-out pt-3',
                  isOpen ? 'max-h-48 opacity-100 pb-5' : 'max-h-0 opacity-0 pb-0'
                )}
                style={{ overflow: 'hidden' }}
              >
                {item.answer}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function FaqPageContent({ initialItems, initialPageTitle }: { initialItems?: FaqItem[]; initialPageTitle?: HeroBannerPageTitle | null } = {}) {
  const t = useTranslations('home.faq');

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <HeroBanner
        page="faq"
        title={t('title')}
        description={t('description')}
        pill={t('pill')}
        initialPageTitle={initialPageTitle}
      />

      <SectionWrapper variant="default" padding="lg" className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <div className="absolute left-10 top-10 h-80 w-80 rounded-full bg-primary-500/10 blur-3xl" />
          <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-blue-500/10 blur-3xl" />
        </div>

        <FaqContent showHeading={false} initialItems={initialItems} />
      </SectionWrapper>
    </div>
  );
}

